import type { CustomerRole } from '@misumi/shared'
import { useSyncExternalStore } from 'react'
import { useAuth } from './auth-context'
import { getSessionState, subscribeSessionState } from './session-store'
import type { AuthSession } from './types'

/**
 * The session seam the four IMTS verticals (WS-2..5) consume (UID-9, session
 * seam IMTS§6). Thin, read-only projections over {@link useAuth} so a vertical
 * screen depends on "who is signed in / what may they do" without re-implementing
 * the auth state machine or reaching into the session store.
 *
 * These deliberately collapse `loading`/`unauthenticated`/`onboarding_required`
 * to `null`: a vertical screen only ever renders behind `RequireAuth` (which
 * handles those states), so from inside a vertical the session is either a
 * fully-authenticated `SessionInfo` or absent. A vertical that needs the finer
 * states uses `useAuth()` directly.
 */

/** The authenticated `SessionInfo`, or `null` when not (yet) authenticated. */
export function useSession(): AuthSession | null {
  const auth = useAuth()
  return auth.status === 'authenticated' ? auth.session : null
}

/**
 * The current session's SERVER-DERIVED customer role, or `null` when not
 * authenticated. Sourced from `SessionInfo.accountContext.role`, which the BFF
 * derives from the session (C2/ADR-0002) — never client-set. Use with
 * `@misumi/shared` `can(role, capability)` for UX gating; the server remains
 * the authorization boundary.
 */
export function useRole(): CustomerRole | null {
  const session = useSession()
  return session?.accountContext.role ?? null
}

/**
 * UID-309 Gate 5 remediation — the SAME capability signal as {@link useRole},
 * for a call site that cannot guarantee an `<AuthProvider>` ancestor. Reads
 * `lib/auth/session-store` DIRECTLY via `useSyncExternalStore` (the same
 * provider-independent posture `rfq-bundle-context.tsx`'s ADR-0103 identity
 * tracking already documents for this exact reason), so it degrades to
 * `null` ("no capability") instead of throwing `useAuth()`'s "must be used
 * within an <AuthProvider>" error.
 *
 * Prefer {@link useRole} for an ordinary vertical screen — those always
 * render behind `<RequireAuth>`, which guarantees `<AuthProvider>` (this
 * file's own doc comment above). Reach for this one only where that
 * guarantee genuinely doesn't hold for the call site — e.g. `PartsLibraryPage`
 * gating its RFQ-bundle banner on `rfq:submit` without pulling a whole-page
 * `<AuthProvider>` dependency (and its `GET /auth/session` probe) into a
 * page whose own tests never needed auth infrastructure before this gate.
 */
export function useRoleWithoutProvider(): CustomerRole | null {
  const state = useSyncExternalStore(subscribeSessionState, getSessionState)
  return state.status === 'authenticated' ? state.session.accountContext.role : null
}

/**
 * UID-430 — the SAME provider-independent posture as {@link useRoleWithoutProvider},
 * for a call site (`GenerateQuoteModal`, the quote-generated confirmation's "emailed
 * to {email}" copy) that is mounted behind `RequireAuth`/`<AuthProvider>` in the real
 * app but is also exercised in unit tests with no such ancestor. Degrades to `null`
 * rather than throwing `useAuth()`'s "must be used within an <AuthProvider>" error.
 */
export function useSessionWithoutProvider(): AuthSession | null {
  const state = useSyncExternalStore(subscribeSessionState, getSessionState)
  return state.status === 'authenticated' ? state.session : null
}
